import { DocsComponentDemo } from "../DocsComponentDemo";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { 
  Sparkles, 
  TrendingUp, 
  AlertTriangle, 
  Briefcase, 
  GraduationCap,
  Globe,
  Award
} from "lucide-react";
import { mockParsedResume } from "../mockData";

export function DemoResumeInsights() {
  const resume = mockParsedResume;

  return (
    <DocsComponentDemo 
      caption="AI-powered resume analysis with skills, scores, and recommendations"
      fullWidth
    >
      <div className="space-y-4">
        {/* AI Summary Section */}
        <div className="p-4 rounded-lg border bg-muted/30 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">AI Summary</span>
            </div>
            <Badge variant="default" className="bg-green-600">
              {resume.hiring_recommendation}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">{resume.ai_summary}</p>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Job Match</span>
            <Progress value={resume.job_match_score || 0} className="flex-1 h-2" />
            <span className="text-sm font-medium text-primary">{resume.job_match_score}%</span>
          </div>
        </div>

        {/* Skills */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Award className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Skills</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {resume.skills.map((skill) => (
              <Badge key={skill} variant="secondary" className="text-xs">
                {skill}
              </Badge>
            ))}
          </div>
        </div>

        {/* Strengths & Concerns */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-1.5 text-green-600">
              <TrendingUp className="h-3.5 w-3.5" />
              <span className="text-xs font-medium">Strengths</span>
            </div>
            <ul className="space-y-1">
              {resume.strengths.slice(0, 3).map((strength, i) => (
                <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                  <span className="text-green-600 mt-0.5">•</span>
                  {strength}
                </li>
              ))}
            </ul>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-1.5 text-amber-600">
              <AlertTriangle className="h-3.5 w-3.5" />
              <span className="text-xs font-medium">Areas to Explore</span>
            </div>
            <ul className="space-y-1">
              {resume.concerns.map((concern, i) => (
                <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                  <span className="text-amber-600 mt-0.5">•</span>
                  {concern}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <Separator />

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Experience</p>
              <p className="text-sm font-medium">{resume.total_years_experience} years</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <GraduationCap className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Education</p>
              <p className="text-sm font-medium">{resume.highest_degree}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Location</p>
              <p className="text-sm font-medium">{resume.extracted_location}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Trajectory</p>
              <p className="text-sm font-medium">{resume.career_trajectory}</p>
            </div>
          </div>
        </div>
      </div>
    </DocsComponentDemo>
  );
}
